// Template catalog for the Ad Strategist. The template registries live on
// the frontend (scenes, product-swap scenes, social JSON templates), so we
// assemble a compact per-route catalog here and send it with the run request.
// The LLM picks a real `template_id` per concept from this catalog, and we
// resolve that id back to a thumbnail / JSON config for the concept mockup +
// the wizard handoff.

import { sceneGroups } from '@/mocks/demo';
import { productSwapScenes } from '@/mocks/app-data';
import { JSON_TEMPLATES } from '@/pages/app/components/social-designs/json-templates/registry';
import type { JsonTemplate } from '@/pages/app/components/social-designs/json-templates/types';

export interface CatalogItem {
  id: string;
  label: string;
  tags: string[];
}

export type TemplateCatalog = Record<string, CatalogItem[]>;

function sceneItems(): CatalogItem[] {
  return sceneGroups.flatMap((g) => g.scenes).map((s) => ({
    id: s.id,
    label: s.name,
    tags: s.tags,
  }));
}

function swapItems(): CatalogItem[] {
  return productSwapScenes.map((s) => ({
    id: s.id,
    label: s.name,
    tags: [...s.silhouettes, ...s.vibe.split(/[^a-zA-Z]+/).filter(Boolean)],
  }));
}

function socialItems(): CatalogItem[] {
  return Object.keys(JSON_TEMPLATES).map((id) => ({ id, label: id, tags: [] }));
}

/** Social templates that PLACE the product in a slot (composeMode !== 'scene-merge').
 *  Scene-merge templates suppress the product slot (the product is meant to be
 *  AI-merged into the bg), so they render blank in a static mockup — exclude
 *  them from the Editable tab. */
export function pasteSocialTemplateIds(): string[] {
  return Object.entries(JSON_TEMPLATES)
    .filter(([, t]) => t.composeMode !== 'scene-merge')
    .map(([id]) => id);
}

/** Per-route catalog sent to the backend brief prompt. Scenes back both the
 *  product-shot and carousel routes. */
export function buildTemplateCatalog(): TemplateCatalog {
  const scenes = sceneItems();
  return {
    'product-shot': scenes,
    'carousel': scenes,
    'product-swap': swapItems(),
    'single': socialItems(),
  };
}

export interface ResolvedTemplate {
  id: string;
  label: string;
  /** Thumbnail path for scene routes. Null for social (rendered live). */
  thumb: string | null;
  /** JSON config for social templates (rendered via JsonSocialPost). */
  jsonConfig: JsonTemplate | null;
}

interface Candidate {
  id: string;
  label: string;
  tags: string[];
  thumb: string | null;
  jsonConfig: JsonTemplate | null;
}

function candidatesFor(route: string): Candidate[] {
  if (route === 'single') {
    return Object.entries(JSON_TEMPLATES).map(([id, cfg]) => ({
      id, label: id, tags: [], thumb: null, jsonConfig: cfg,
    }));
  }
  if (route === 'product-swap') {
    return productSwapScenes.map((s) => ({
      id: s.id,
      label: s.name,
      tags: [...s.silhouettes, ...s.vibe.split(/[^a-zA-Z]+/).filter(Boolean)],
      thumb: s.thumb,
      jsonConfig: null,
    }));
  }
  return sceneGroups.flatMap((g) => g.scenes).map((s) => ({
    id: s.id, label: s.name, tags: s.tags, thumb: s.image, jsonConfig: null,
  }));
}

/** Resolve a concept's (route, template_id) to a renderable template. Lenient
 *  on purpose — the LLM's template_id can be slightly off (a name, odd casing,
 *  or a near-miss), so we fall back through name match → hint/tag overlap →
 *  the first template, guaranteeing every concept shows a real template rather
 *  than a blank card. Returns null only when a route has no templates. */
export function resolveTemplate(
  route: string,
  id: string | undefined | null,
  hint?: string,
): ResolvedTemplate | null {
  const candidates = candidatesFor(route);
  if (candidates.length === 0) return null;

  const shape = (c: Candidate): ResolvedTemplate => ({
    id: c.id, label: c.label, thumb: c.thumb, jsonConfig: c.jsonConfig,
  });

  // 1. exact id
  if (id) {
    const exact = candidates.find((c) => c.id === id);
    if (exact) return shape(exact);
    // 2. name / case-insensitive id match (LLM may return the label)
    const idl = id.trim().toLowerCase();
    const byName = candidates.find(
      (c) => c.id.toLowerCase() === idl || c.label.toLowerCase() === idl,
    );
    if (byName) return shape(byName);
  }

  // 3. fuzzy match on the visual hint against tags + label
  if (hint) {
    const terms = hint.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 2);
    let best: Candidate | null = null;
    let bestScore = 0;
    for (const c of candidates) {
      const tagScore = c.tags.reduce((acc, t) => acc + (terms.includes(t.toLowerCase()) ? 1 : 0), 0);
      const labelScore = terms.some((t) => c.label.toLowerCase().includes(t)) ? 1 : 0;
      const s = tagScore + labelScore;
      if (s > bestScore) { bestScore = s; best = c; }
    }
    if (best) return shape(best);
  }

  // 4. deterministic default — better a real template than a blank card
  return shape(candidates[0]);
}
